import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChangeEvent, FormEvent, useContext, useEffect, useState } from "react";
import { Badge, Button, Col, Dropdown, Form, Row } from "react-bootstrap";
import DropdownItem from "react-bootstrap/DropdownItem";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { congregationApi, useCongSettingsSuspense } from "../../../api/cong";
import { usePrivilegeMap } from "../../../api/meetings";
import QueryKeys from "../../../api/queryKeys";
import { HGBugsnagNotify } from "../../../helpers/bugsnag";
import HourglassGlobals, { HGContext } from "../../../helpers/globals";
import { getStoredLangGroupId } from "../../../helpers/langGroups";
import { userCompare } from "../../../helpers/user";
import { numberArrayRange } from "../../../helpers/util";
import { Permission } from "../../../types/permission";
import { UserPrivileges } from "../../../types/scheduling/privileges";
import { CongSetting, CongSettingMap, CongSettings } from "../../../types/scheduling/settings";
import User from "../../../types/user";
import { TemplateTextEditor } from "../../common/tiptap/TemplateEditor";
import { TemplateVariable } from "../../common/tiptap/TemplateVariable";
import { SaveResult, SaveStatus } from "../../saveStatus";
import { HGPageTitle, InfoPopover } from "../../utils";
import { useUserMap } from "../common";
import { LanguageGroupDDM } from "../languageGroup";
import { SettingsWM } from "./weekend";
import { SettingsCleaning } from "./cleaning";
import { SettingsPW } from "./publicWitnessing";
import { SettingsAVA } from "./ava";
import { SettingsMM } from "./midweek";

export function canUpdateCongSettings(): boolean {
  return HourglassGlobals.permissions.has(Permission.UpdateCongregation);
}

export function validateCustomMapProviderSettings(
  settings: CongSettings,
  tileUrlRef: React.RefObject<HTMLInputElement | null> | null,
  styleUrlRef: React.RefObject<HTMLInputElement | null> | null,
  t: TFunction,
): boolean {
  // Clear any previous custom validity messages
  tileUrlRef?.current?.setCustomValidity("");
  styleUrlRef?.current?.setCustomValidity("");

  // Only validate if custom map provider is selected
  if (settings.terr_tile_provider !== 999) {
    return true;
  }

  const tileUrl = settings.terr_custom_tile_provider_tile_url;
  const styleUrl = settings.terr_custom_tile_provider_style_url;

  // Validate tile URL
  if (!tileUrl || !tileUrl.startsWith("https://")) {
    tileUrlRef?.current?.setCustomValidity(t("popup.error.title.invalid-input"));
    tileUrlRef?.current?.reportValidity();
    return false;
  }
  if (!tileUrl.includes("{x}") || !tileUrl.includes("{y}")) {
    tileUrlRef?.current?.setCustomValidity(t("popup.error.title.invalid-input"));
    tileUrlRef?.current?.reportValidity();
    return false;
  }

  // Validate style URL
  if (!styleUrl || !styleUrl.startsWith("https://")) {
    styleUrlRef?.current?.setCustomValidity(t("popup.error.title.invalid-input"));
    styleUrlRef?.current?.reportValidity();
    return false;
  }

  return true;
}

export default function Settings() {
  const { t } = useTranslation();
  const [saveResult, setSaveResult] = useState<SaveResult>(SaveResult.None);
  const [langGroupId, setLangGroupId] = useState(getStoredLangGroupId());
  const saveSettingsMutation = useMutation({
    mutationFn: (settings: CongSettings) => congregationApi.setSettings(settings, langGroupId),
  });
  const queryClient = useQueryClient();
  const ctx = useContext(HGContext);
  const hostCong = ctx.globals.cong!;

  const congSettingsQuery = useCongSettingsSuspense(langGroupId);
  const [settings, setSettings] = useState(congSettingsQuery.data);

  const handleReset = () => {
    setSettings(congSettingsQuery.data);
  };

  // we only want this to run when the language group changes
  /* oxlint-disable react-hooks/exhaustive-deps -- intentionally excluding congSettingsQuery.data: it can change
     reference on refetch/invalidation (e.g. after handleUpdate saves), and we don't want that to clobber the
     user's in-progress edits to `settings`; this should only re-sync when the language group itself changes */
  useEffect(() => {
    setSettings(congSettingsQuery.data);
  }, [langGroupId]);
  /* oxlint-enable react-hooks/exhaustive-deps */

  const handleUpdate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const form = e.currentTarget;
    if (!form.checkValidity()) return;

    try {
      const resp = await saveSettingsMutation.mutateAsync(settings);
      setSaveResult(SaveResult.Success);
      await Promise.all([
        queryClient.setQueryData([QueryKeys.CongregationSettings, langGroupId], resp),
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.WMCSchedules],
        }),
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.AVAttendantAssignment],
        }),
      ]);
    } catch (err: any) {
      setSaveResult(SaveResult.Failure);
      console.error("error saving scheduling settings", err);
      HGBugsnagNotify("schedulingSettings", err);
    }
  };

  return (
    <div>
      <div className="d-flex flex-row justify-content-between align-items-end mb-4">
        <HGPageTitle title={t("schedules.settings.title")} />
        <LanguageGroupDDM langGroupId={langGroupId} setLangGroupId={setLangGroupId} />
      </div>
      <Form className="my-4 d-flex flex-column gap-4" onSubmit={handleUpdate}>
        <div className="card">
          <div className="card-body">
            <h5>{t("conganalysis.attendance.weekend")}</h5>
            <hr className="mt-3" />
            <SettingsWM settings={settings} setSettings={setSettings} myCong={hostCong} lgroup={langGroupId} />
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <h5>{t("conganalysis.attendance.midweek")}</h5>
            <hr className="mt-3" />
            <SettingsMM settings={settings} setSettings={setSettings} showCOName={false} />
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <h5>{t("schedules.ava.title")}</h5>
            <hr className="mt-3" />
            <SettingsAVA settings={settings} setSettings={setSettings} />
          </div>
        </div>

        {langGroupId === hostCong.id && (
          <>
            <div className="card">
              <div className="card-body">
                <h5>{t("schedules.cleaning.title")}</h5>
                <hr className="mt-3" />
                <SettingsCleaning settings={settings} setSettings={setSettings} />
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <h5>{t("schedules.public-witnessing.title")}</h5>
                <hr className="mt-3" />
                <SettingsPW settings={settings} setSettings={setSettings} />
              </div>
            </div>
          </>
        )}

        <div className="mt-4">
          <Button variant="secondary" onClick={handleReset} disabled={!canUpdateCongSettings()}>
            {t("general.reset")}
          </Button>
          <Button
            variant="primary"
            type="submit"
            className="ms-1 me-1"
            disabled={settings === congSettingsQuery.data || !canUpdateCongSettings()}
          >
            {t("general.save")}
          </Button>
          <SaveStatus saveResult={saveResult} saveKey="sched_settings" setSaveResult={setSaveResult} />
        </div>
      </Form>
    </div>
  );
}

export function DDMstrings(props: {
  settings: CongSettings;
  setSettings: (set: CongSettings) => void;
  settingKey: CongSetting;
  options: { label: string; value: string }[];
  labelSuffix?: string;
  infoMsg?: string;
  coerceToNumber?: boolean;
}) {
  const { t } = useTranslation();
  const myCong = useContext(HGContext).globals.cong!;

  const change = (e: ChangeEvent<HTMLSelectElement>) => {
    const val = props.coerceToNumber ? parseInt(e.currentTarget.value) : e.currentTarget.value;
    props.setSettings({
      ...props.settings,
      [props.settingKey]: val,
    });
  };

  let val = props.settings[props.settingKey];
  // the default for the paper size setting needs to be the country's paper size
  if (!val && props.settingKey === CongSetting.PaperSize) val = myCong.country.paper_size;

  return (
    <Form.Group as={Row} className="mt-1" controlId={`setting-${props.settingKey}`}>
      <Form.Label column>
        {t(CongSettingMap[props.settingKey])}
        {props.labelSuffix && " (" + props.labelSuffix + ")"}
        {!!props.infoMsg && <InfoPopover text={props.infoMsg} />}
      </Form.Label>
      <Col>
        <Form.Select value={val as string} onChange={change}>
          {props.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Form.Select>
      </Col>
    </Form.Group>
  );
}

export function DDMnumbers(props: {
  settings: CongSettings;
  setSettings: (set: CongSettings) => void;
  settingKey: CongSetting;
  options: number;
  exactOptions?: number[];
  allowZero?: boolean;
  zeroIsBlank?: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation();

  const change = (e: ChangeEvent<HTMLSelectElement>) => {
    const intVal = parseInt(e.currentTarget.value);
    if (isNaN(intVal)) return;
    props.setSettings({
      ...props.settings,
      [props.settingKey]: intVal,
    });
  };

  const options = props.exactOptions ?? numberArrayRange(1, props.options);

  return (
    <Form.Group as={Row} className="mt-1" controlId={`setting-${props.settingKey}`}>
      <Form.Label column>{t(CongSettingMap[props.settingKey])}</Form.Label>
      <Col>
        <Form.Select
          value={(props.settings[props.settingKey] as number) || ""}
          onChange={change}
          disabled={props.disabled}
        >
          {props.allowZero && <option key={0} label={props.zeroIsBlank ? "" : "0"} value={0} />}
          {options.map((v, i) => (
            <option key={i} value={v}>
              {v}
            </option>
          ))}
        </Form.Select>
      </Col>
    </Form.Group>
  );
}

export function TextInput(props: {
  settings: CongSettings;
  setSettings: (set: CongSettings) => void;
  settingKey: CongSetting;
  pill?: number;
  placeholder?: string;
  hidden?: boolean;
  minLength?: number;
  maxLength?: number;
  inputType?: string;
  min?: number;
  max?: number;
  disabled?: boolean;
  infoMsg?: string;
  pattern?: string;
  required?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}) {
  const { t } = useTranslation();
  const isNumber = props.inputType === "number";
  // While actively editing a number field, buffer the raw text locally so the
  // field can go empty without being clamped back to `min` on every keystroke.
  const [rawValue, setRawValue] = useState<string | null>(null);

  const displayValue = isNumber && rawValue !== null ? rawValue : (props.settings[props.settingKey] as string);

  return (
    <Form.Group as={Row} className="mt-1" hidden={props.hidden} controlId={`setting-${props.settingKey}`}>
      <Form.Label column>
        {t(CongSettingMap[props.settingKey])}
        {!!props.pill && (
          <Badge pill bg="secondary" className="ms-1">
            {props.pill}
          </Badge>
        )}
        {!!props.infoMsg && <InfoPopover text={props.infoMsg} />}
      </Form.Label>
      <Col>
        <Form.Control
          as="input"
          ref={props.inputRef}
          placeholder={props.placeholder ?? ""}
          value={displayValue}
          type={props.inputType || "text"}
          minLength={props.minLength ?? 0}
          maxLength={props.maxLength}
          min={props.min}
          max={props.max}
          disabled={props.disabled}
          pattern={props.pattern}
          required={props.required}
          onChange={(e) => {
            // Clear custom validity when user starts typing
            if (props.inputRef?.current) {
              props.inputRef.current.setCustomValidity("");
            }
            if (isNumber) {
              setRawValue(e.target.value);
              if (e.target.value === "") return;
              const parsed = Number(e.target.value);
              if (!isNaN(parsed)) {
                props.setSettings({
                  ...props.settings,
                  [props.settingKey]: parsed,
                });
              }
              return;
            }
            props.setSettings({
              ...props.settings,
              [props.settingKey]: e.target.value,
            });
          }}
          onBlur={() => {
            if (!isNumber || rawValue === null) return;
            let parsed = Number(rawValue);
            if (rawValue === "" || isNaN(parsed)) parsed = props.min ?? 0;
            if (typeof props.min === "number" && parsed < props.min) parsed = props.min;
            if (typeof props.max === "number" && parsed > props.max) parsed = props.max;
            setRawValue(null);
            props.setSettings({
              ...props.settings,
              [props.settingKey]: parsed,
            });
          }}
        />
      </Col>
    </Form.Group>
  );
}

export function TextArea(props: {
  settings: CongSettings;
  setSettings: (set: CongSettings) => void;
  settingKey: CongSetting;
  labelIcon?: React.JSX.Element;
}) {
  const { t } = useTranslation();

  return (
    <Form.Group as={Row} className="mt-1" controlId={`setting-${props.settingKey}`}>
      <Form.Label column>
        {t(CongSettingMap[props.settingKey])} {props.labelIcon}
      </Form.Label>
      <Col>
        <Form.Control
          as="textarea"
          rows={4}
          value={props.settings[props.settingKey] as string}
          onChange={(e) => {
            props.setSettings({
              ...props.settings,
              [props.settingKey]: e.target.value,
            });
          }}
        />
      </Col>
    </Form.Group>
  );
}

export function TemplateEditor(props: {
  settings: CongSettings;
  content: string;
  setSettings: (set: CongSettings) => void;
  settingKey: CongSetting;
  variables: TemplateVariable[];
}) {
  const { t } = useTranslation();
  return (
    <Form.Group as={Row} className="mt-1" controlId={`setting-${props.settingKey}`}>
      <Form.Label column>{t(CongSettingMap[props.settingKey])}</Form.Label>
      <Col>
        <TemplateTextEditor
          value={props.content}
          variables={props.variables}
          onChange={(value) => {
            props.setSettings({
              ...props.settings,
              [props.settingKey]: value,
            });
          }}
        />
      </Col>
    </Form.Group>
  );
}

export function CheckBox(props: {
  settings: CongSettings;
  setSettings: (set: CongSettings) => void;
  settingKey: CongSetting;
  switchType?: boolean;
  infoMsg?: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <Form.Group as={Row} className="mt-1" controlId={`setting-${props.settingKey}`}>
      <Form.Label column>
        {t(CongSettingMap[props.settingKey])}
        {!!props.infoMsg && <InfoPopover text={props.infoMsg} />}
      </Form.Label>
      <Col>
        <Form.Check
          as="input"
          className="fs-4"
          disabled={props.disabled === true}
          type={props.switchType ? "switch" : undefined}
          checked={props.settings[props.settingKey] as boolean}
          onChange={(e) => {
            props.setSettings({
              ...props.settings,
              [props.settingKey]: e.target.checked,
            });
          }}
        />
      </Col>
    </Form.Group>
  );
}

export function UserDropdown(props: {
  settings: CongSettings;
  setSettings: (set: CongSettings) => void;
  settingKey: CongSetting;
  privilege: keyof UserPrivileges;
  lgroup: number;
}) {
  const { t } = useTranslation();
  const users = useUserMap();
  const privilegeMap = usePrivilegeMap(props.lgroup);

  const currentAssignee = props.settings[props.settingKey];
  const assignee = typeof currentAssignee === "number" ? users.get(currentAssignee) : undefined;

  const setUser = (userId: number | undefined) => {
    const settings = { ...props.settings, [props.settingKey]: userId };
    props.setSettings(settings);
  };

  const privMapData = privilegeMap.data ? privilegeMap.data[props.settingKey] : undefined;
  const possibleUsers = privMapData
    ? privMapData
        .map((userId) => users.get(userId))
        .filter((u): u is User => !!u)
        .sort(userCompare)
    : assignee
      ? [assignee]
      : [];

  return (
    <Form.Group as={Row} className="mt-1" controlId={`setting-${props.settingKey}`}>
      <Form.Label column>{t(CongSettingMap[props.settingKey])}</Form.Label>
      <Col>
        <Dropdown className="dropdown-bounded">
          <Dropdown.Toggle variant="secondary">
            {assignee?.displayName || <i>{t("general.none-selected")}</i>}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {!!assignee && (
              <>
                <Dropdown.Item onClick={() => setUser(undefined)}>
                  ❌ <i>{t("schedules.fill.clear")}</i>
                </Dropdown.Item>
                <Dropdown.Divider />
              </>
            )}
            {possibleUsers
              .filter((u) => assignee?.id !== u.id)
              .map((u) => (
                <DropdownItem key={u.id} onClick={() => setUser(u.id)}>
                  {u.displayName}
                </DropdownItem>
              ))}
          </Dropdown.Menu>
        </Dropdown>
      </Col>
    </Form.Group>
  );
}
